import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Share2, Clock, Briefcase, DollarSign, CalendarDays, Loader2, Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { VenueCard } from "../components/Cards";
import { PipeText } from "../lib/pipeText";

const inp = "w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-rose-500 text-sm";

function ApplyForm({ job }) {
  const { user } = useAuth();
  const [f, setF] = useState({ full_name: user?.name || "", email: user?.email || "", phone: "", city: "", state: "", cover_message: "", references: "", portfolio_link: "", availability: "", experience_summary: "", resume_path: "", resume_name: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const onResume = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/hiring/upload-resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("resume_path", data.resume_path); set("resume_name", data.resume_name);
      toast.success("Resume attached");
    } catch (err) { toast.error(errMsg(err)); }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post(`/hiring/jobs/${job.id}/apply`, f); setDone(true); toast.success("Application submitted!"); }
    catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  if (done) return (
    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5 text-center" data-testid="apply-done">
      <CheckCircle2 className="mx-auto text-emerald-400 mb-2" size={28} />
      <p className="font-bold text-emerald-300">Application submitted!</p>
      <p className="text-sm text-zinc-400 mt-1">{user ? "Track it under Dashboard → My Applications." : "The employer will contact you by email."}</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3" data-testid="apply-form">
      <input required className={inp} placeholder="Full name *" value={f.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="apply-name" />
      <input required type="email" className={inp} placeholder="Email *" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="apply-email" />
      <input className={inp} placeholder="Phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="apply-phone" />
      <input className={inp} placeholder="City, State" value={f.city} onChange={(e) => set("city", e.target.value)} />
      <label className="sm:col-span-2 border-2 border-dashed border-white/15 rounded-xl p-4 text-center cursor-pointer hover:border-rose-500/50 transition-colors">
        <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={onResume} data-testid="apply-resume-input" />
        {uploading ? <span className="text-sm text-zinc-400 flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Uploading…</span>
          : f.resume_name ? <span className="text-sm text-emerald-300 flex items-center justify-center gap-2"><CheckCircle2 size={14} /> {f.resume_name}</span>
            : <span className="text-sm text-zinc-400 flex items-center justify-center gap-2"><Upload size={14} /> Upload resume (PDF, DOC, DOCX)</span>}
      </label>
      <input className={`${inp} sm:col-span-2`} placeholder="Portfolio or website link" value={f.portfolio_link} onChange={(e) => set("portfolio_link", e.target.value)} />
      <input className={`${inp} sm:col-span-2`} placeholder="Availability (e.g. weekends, evenings)" value={f.availability} onChange={(e) => set("availability", e.target.value)} />
      <textarea rows={2} className={`${inp} sm:col-span-2`} placeholder="Experience summary" value={f.experience_summary} onChange={(e) => set("experience_summary", e.target.value)} data-testid="apply-experience" />
      <textarea rows={2} className={`${inp} sm:col-span-2`} placeholder="References" value={f.references} onChange={(e) => set("references", e.target.value)} />
      <textarea rows={3} className={`${inp} sm:col-span-2`} placeholder="Cover message" value={f.cover_message} onChange={(e) => set("cover_message", e.target.value)} data-testid="apply-message" />
      <button disabled={busy} className="sm:col-span-2 bg-rose-500 hover:bg-rose-600 font-bold py-3 rounded-lg disabled:opacity-40 flex items-center justify-center gap-2" data-testid="apply-submit">
        {busy && <Loader2 size={15} className="animate-spin" />} Submit application
      </button>
    </form>
  );
}

const Fact = ({ Icon, l, v, className = "" }) => v ? (
  <div className={`flex items-start gap-2 text-sm ${className}`}><Icon size={15} className="text-rose-400 shrink-0 mt-0.5" /><span className="text-zinc-500 shrink-0">{l}:</span><span className="text-zinc-200 min-w-0">{v}</span></div>
) : null;

export default function HiringDetail() {
  const { slug } = useParams();
  const [j, setJ] = useState(undefined);
  useEffect(() => { api.get(`/hiring/jobs/${slug}`).then((r) => setJ(r.data)).catch(() => setJ(null)); window.scrollTo(0, 0); }, [slug]);
  useEffect(() => { if (j) document.title = j.seo_title || `${j.title} — Hiring Hub · Gulf Coast VIP`; }, [j]);

  if (j === undefined) return <div className="py-40 flex justify-center text-zinc-500"><Loader2 className="animate-spin" /></div>;
  if (!j) return <div className="py-40 text-center text-zinc-400">Job not found. <Link to="/hiring" className="text-rose-400">Browse jobs →</Link></div>;

  const share = () => {
    if (navigator.share) navigator.share({ title: j.title, url: window.location.href }).catch(() => {});
    else { navigator.clipboard.writeText(window.location.href); toast("Link copied"); }
  };
  const open = j.status === "open";

  return (
    <div className="px-6 md:px-12 lg:px-20 py-10 max-w-6xl mx-auto" data-testid="hiring-detail-page">
      <div className="grid lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-7">
          <p className="overline mb-2">{j.job_category || "Hospitality"} · {j.job_type || "Job"}</p>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl" data-testid="job-title">{j.title}</h1>
          <p className="text-zinc-300 mt-2 text-lg">{j.business_name || j.venue_name}</p>
          <p className="text-sm text-zinc-500 flex items-center gap-1 mt-1"><MapPin size={13} /> {[j.address, j.city, j.state].filter(Boolean).join(", ") || "Gulf Coast"}{j.remote_onsite ? ` · ${j.remote_onsite}` : ""}</p>
          {!open && <p className="mt-4 bg-amber-500/10 border border-amber-500/30 text-amber-300 rounded-lg px-4 py-2 text-sm capitalize" data-testid="job-closed-banner">This position is {j.status.replace(/_/g, " ")}.</p>}
          <div className="mt-6 grid sm:grid-cols-2 gap-3 bg-[#111] border border-white/10 rounded-xl p-5">
            <Fact Icon={DollarSign} l="Pay" v={[j.pay_range, j.pay_type].filter(Boolean).join(" · ")} />
            <Fact Icon={CalendarDays} l="Starts" v={j.start_date} />
            <Fact Icon={Clock} l="Schedule" v={j.schedule} className="sm:col-span-2" />
            <Fact Icon={Briefcase} l="Experience" v={j.experience_required} className="sm:col-span-2" />
            <Fact Icon={CalendarDays} l="Apply by" v={j.application_deadline} />
          </div>
          {j.description && <div className="mt-6"><p className="overline mb-2">About the job</p><PipeText text={j.description} className="text-zinc-300" /></div>}
          {j.responsibilities && <div className="mt-5"><p className="overline mb-2">Responsibilities</p><PipeText text={j.responsibilities} className="text-zinc-400" /></div>}
          {j.requirements && <div className="mt-5"><p className="overline mb-2">Requirements</p><PipeText text={j.requirements} className="text-zinc-400" /></div>}
          <button onClick={share} className="mt-6 border border-white/15 px-5 py-2.5 rounded-lg text-sm flex items-center gap-2 hover:bg-white/5" data-testid="job-share"><Share2 size={14} /> Share this job</button>
          {j.venue && (
            <div className="mt-10">
              <p className="overline mb-4">At this venue</p>
              <div className="max-w-sm"><VenueCard v={j.venue} /></div>
            </div>
          )}
        </div>
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <div className="bg-[#111] border border-rose-500/30 rounded-2xl p-6">
            <h3 className="font-display font-bold text-xl mb-4">Apply for this job</h3>
            {open ? <ApplyForm job={j} /> : <p className="text-zinc-500 text-sm">Applications are closed for this position.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
